import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { UserState } from "redux-oidc";
import { CalendarPickerInput } from "@cadmakers-ui/core";
import { RouteComponentProps, withRouter } from "react-router";
import { Button, Col, Form, FormGroup, FormText, Label, Row } from "reactstrap";
import { Application } from "../../../App/Models/Application";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import ResponseHandler from "../../../App/ResponseHandler";
import { ApiRequest, appPaths, IAppState } from "../../../Common";
import { endpoints, SubmitFeatureRequest } from "../..";
import { updateFeatureRequest } from "../../Actions/FeedbackFormActions";
import { FeedbackCategory, FeedbackParent, FeedbackSize } from "../../Constants/feedbackEnums";
import FileUploadButton from "../../../Common/Components/FileUploadButton/FileUploadButton";
import {
  fileService,
  imageUploadService,
  feedbackService,
  fileUploadService
} from "../../Services";
import ElementSpacer from "../../../Layout/Components/ElementSpacer";
import ImageDropzone from "../Common/ImageDropzone/ImageDropzone";
import FeedbackFileManager from "../Common/FeedbackFileManager/FeedbackFileManager";
import { Topic } from "../../../Topic/Models/Topic";
import { updateTopics } from "../../../Startup/Actions/StartupActions";
import { FeedbackFormInput } from "../Common/FeedbackFormInput";
import { Image } from "../../Models/Image";
import { DeleteImage } from "../../Models/DeleteImage";
import { DeleteFile } from "../../Models/DeleteFile";
import SimilarFeedbackSuggestionsContainer from "../Common/SimilarFeedbackSuggestions/SimilarFeedbackSuggestionsContainer";
import { feedbackFormConstants } from "../../Constants/feedbackFormConstants";
import { FeedbackFile } from "../../Models/FeedbackFile";
import FeedbackFormTagsInput from "../Common/FeedbackFormTagsInput";
import FeedbackFormIsPrivateInput from "../Common/FeedbackFormIsPrivateInput";
import { userService } from "../../../User";
import { FeedbackSizeDropdown } from "../Common/FeedbackDropdowns/FeedbackSizeDropdown";

type Props = {
  selectedApp?: Application;
  selectedTopic?: Topic;
  selectedParent: FeedbackParent;
  code: string;
};

type StateProps = {
  featureRequest: SubmitFeatureRequest;
  topics: Topic[];
  user: UserState;
};

type DispatchProps = {
  updateFeatureRequest: (featureRequest: SubmitFeatureRequest) => void;
  showSnack: (uniqueId: any, args: any) => void;
  updateTopics: (topics: Topic[]) => void;
};

type State = {
  featureRequest: SubmitFeatureRequest;
  canSubmit: boolean;
  images: Image[];
  files: FeedbackFile[];
  dueDateDatePickerActual: Date;
  startDateDatePickerActual: Date;
  selectedSize?: FeedbackSize;
};

const mapStateToProps = (state: IAppState) => ({
  featureRequest: state.feedbackForm.featureRequest,
  topics: state.app.topics,
  user: state.oidc
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updateFeatureRequest: (featureRequest: SubmitFeatureRequest) =>
    dispatch(updateFeatureRequest(featureRequest)),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args)),
  updateTopics: (topics: Topic[]) => dispatch(updateTopics(topics))
});

class FeatureRequestForm extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  private imageUploadPendingCount: number;

  private fileUploadPendingCount: number;

  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    this.state = {
      featureRequest: this.props.featureRequest,
      canSubmit: true,
      images: [],
      files: [],
      dueDateDatePickerActual: new Date(),
      startDateDatePickerActual: new Date(),
      selectedSize: undefined
    };

    this.imageUploadPendingCount = 0;
    this.fileUploadPendingCount = 0;
  }

  componentWillUnmount = () => {
    this.props.updateFeatureRequest(this.state.featureRequest);
  };

  getFeatureRequestBody = () => {
    return {
      title: this.state.featureRequest.title,
      appRolePrefix: this.props.selectedApp ? this.props.selectedApp.rolePrefix : null,
      topicCode: this.props.selectedTopic ? this.props.selectedTopic.code : null,
      feedbackParent: this.props.selectedParent,
      feedbackCategory: FeedbackCategory[FeedbackCategory.FeatureRequest],
      description: this.state.featureRequest.description,
      featureRequirements: this.state.featureRequest.featureRequirements,
      imageRefs: this.state.images,
      files: this.state.files,
      tagIds: this.state.featureRequest.tagIds,
      isPrivate: this.state.featureRequest.isPrivate,
      dueDate: this.state.featureRequest.dueDate,
      startDate: this.state.featureRequest.startDate,
      estimatedHours: this.state.featureRequest.estimatedHours,
      feedbackSize: this.state.selectedSize
    };
  };

  clearForm = () => {
    this.setState({
      featureRequest: {
        title: "",
        description: "",
        featureRequirements: "",
        tagIds: [],
        isPrivate: true,
        estimatedHours: undefined
      }
    });
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const { name } = event.target;

    if (name === "estimatedHours") {
      this.setState(prevState => ({
        featureRequest: { ...prevState.featureRequest, [name as any]: parseInt(value, 10) }
      }));
    } else {
      this.setState(prevState => ({
        featureRequest: { ...prevState.featureRequest, [name as any]: value }
      }));
    }
  };

  onChangeTags = (tagIds: number[]) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, tagIds }
    }));
  };

  onChangeIsPrivate = (isPrivate: boolean) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, isPrivate }
    }));
  };

  onChangeDueDate = (dueDate: Date) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, dueDate }
    }));
  };

  onChangeDueDatepickerActual = (actual: Date) => {
    this.setState(prevState => ({
      dueDateDatePickerActual: actual
    }));
  };

  onChangeStartDate = (startDate: Date) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, startDate }
    }));
  };

  onChangeStartDatepickerActual = (actual: Date) => {
    this.setState(prevState => ({
      startDateDatePickerActual: actual
    }));
  };

  onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.imageUploadPendingCount !== 0) {
      this.showErrorSnackbar(
        "Failed to submit feature request",
        "Please wait until all images are finished uploading"
      );
      return;
    }

    this.setState({
      canSubmit: false
    });

    let redirect: boolean = false;
    ApiRequest.fromPath(endpoints.feedback.create(this.props.code))
      .withAuthHeader()
      .withJson(this.getFeatureRequestBody())
      .post()
      .then(async (response: Response) => {
        await ResponseHandler.handle(
          response,
          () => {
            if (this.props.selectedParent === FeedbackParent.Topic) {
              const topicsCopy = [...this.props.topics];
              const topicIndex = topicsCopy.findIndex(
                topic => (this.props.selectedTopic as Topic).code === topic.code
              );

              if (topicIndex !== -1) {
                topicsCopy[topicIndex].featureRequestCount++;
              }

              this.props.updateTopics(topicsCopy);
            }

            this.clearForm();
            redirect = true;
          },
          (result: ResponseResult) => {
            this.showErrorSnackbar(
              "Failed to create feature request",
              result.error.message,
              response.status
            );
          },
          () => {
            this.showErrorSnackbar(
              "Failed to create feature request",
              "An unknown error has occured",
              response.status
            );
          }
        );
      })
      .finally(() => {
        this.setState({
          canSubmit: true
        });

        if (redirect) {
          switch (this.props.selectedParent) {
            case FeedbackParent.Application:
              this.props.history.push(
                feedbackService.getViewUrlForCodeAndAppRolePrefix(
                  this.props.code,
                  (this.props.selectedApp as Application).rolePrefix,
                  FeedbackCategory.FeatureRequest
                )
              );
              break;
            case FeedbackParent.Topic:
              this.props.history.push(
                feedbackService.getViewUrlForCodeAndTopicCode(
                  this.props.code,
                  (this.props.selectedTopic as Topic).code,
                  FeedbackCategory.FeatureRequest
                )
              );
              break;
            default:
              break;
          }
        }
      });
  };

  onImagesAdded = (images: Image[]) => {
    if (this.props.code) {
      this.imageUploadPendingCount += images.length;

      images.forEach(async image => {
        await imageUploadService.uploadImage(
          endpoints.feedback.images.uploadTemp(this.props.code),
          image,
          newImage => {
            this.setState(prevState => ({
              images: prevState.images.concat(newImage)
            }));
          },
          hash => {
            return this.state.images.find(x => x.fullSizeHash === hash);
          },
          () => {
            this.showErrorSnackbar(
              "Failed to upload image",
              "This image has already been uploaded"
            );
          },
          (response, result) => {
            this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
          },
          response => {
            this.showErrorSnackbar(
              "Failed to upload image",
              "An unknown error has occured",
              response.status
            );
          }
        );

        this.imageUploadPendingCount--;
      });
    } else {
      this.showErrorSnackbar("Failed to upload image", "Feedback code was not generated");
    }
  };

  onImageDelete = (image: Image) => {
    const deleteModel: DeleteImage = {
      fullSizeObjectPath: image.fullSizeObjectPath,
      thumbnailObjectPath: image.thumbnailObjectPath
    };

    imageUploadService.deleteImage(
      endpoints.feedback.images.deleteTemp(),
      deleteModel,
      () => {
        this.setState(prevState => ({
          images: prevState.images.filter(i => i !== image)
        }));
      },
      (response, result) => {
        this.showErrorSnackbar("Failed to remove image", result.error.message, response.status);
      },
      response => {
        this.showErrorSnackbar(
          "Failed to remove image",
          "An unknown error has occured",
          response.status
        );
      }
    );
  };

  onFilesAdded = (files: FeedbackFile[], isCadmakersOnly: boolean) => {
    if (this.props.code) {
      this.fileUploadPendingCount += files.length;

      files.forEach(async file => {
        await fileUploadService.uploadFile(
          endpoints.feedback.files.uploadTemp(this.props.code, isCadmakersOnly),
          file,
          newFile => {
            this.setState(prevState => ({
              files: prevState.files.concat(newFile)
            }));
          },
          hash => {
            return this.state.files.find(x => x.hash === hash);
          },
          () => {
            this.showErrorSnackbar("Failed to upload file", "This file has already been uploaded");
          },
          (response, result) => {
            this.showErrorSnackbar(
              "Failed to upload file",
              result?.error?.message ?? "",
              response.status
            );
          },
          response => {
            this.showErrorSnackbar(
              "Failed to upload file",
              "An unknown error has occured",
              response.status
            );
          }
        );

        this.fileUploadPendingCount--;
      });
    } else {
      this.showErrorSnackbar("Failed to upload file", "Feedback code was not generated");
    }
  };

  onFileDelete = (file: FeedbackFile) => {
    const deleteModel: DeleteFile = {
      path: file.path
    };

    fileUploadService.deleteFile(
      endpoints.feedback.files.deleteTemp(),
      deleteModel,
      () => {
        this.setState(prevState => ({
          files: prevState.files.filter(f => f !== file)
        }));
      },
      (response, result) => {
        this.showErrorSnackbar("Failed to remove file", result.error.message, response.status);
      },
      response => {
        this.showErrorSnackbar(
          "Failed to remove file",
          "An unknown error has occured",
          response.status
        );
      }
    );
  };

  onImageUploadClick = (files: File[]) => {
    this.onImagesAdded(fileService.filestoImages(files));
  };

  onFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, false), false);
  };

  onCadmakersFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, true), true);
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_BOX", {
      label,
      timeout: 5000
    });
  };

  getSizeoptions = () => {
    const options = [FeedbackSize.Small];
    options.push(FeedbackSize.Medium);
    options.push(FeedbackSize.Large);
    return options;
  };

  onSizeSelected = (selected?: FeedbackSize) => {
    if (selected) {
      this.setState({
        selectedSize: selected
      });
    }
  };

  render() {
    const isCadmakers = userService.isUserCadmakers(this.props.user);

    return (
      <Form onSubmit={e => this.onSubmit(e)}>
        <Row>
          <Col lg={6}>
            <FeedbackFormInput
              label=" Title (Brief Description)"
              type="text"
              name="title"
              autoComplete="off"
              required
              value={this.state.featureRequest.title}
              onChange={e => this.onChange(e)}
              description="This is the displayed title for the feature request. Please make it concise."
            />
          </Col>
          {this.state.featureRequest.title.length >=
            feedbackFormConstants.minLengthForSimilarNames && (
            <Col lg={6}>
              <SimilarFeedbackSuggestionsContainer
                query={this.state.featureRequest.title}
                selectedParent={this.props.selectedParent}
                selectedApp={this.props.selectedApp}
                selectedTopic={this.props.selectedTopic}
              />
            </Col>
          )}
        </Row>
        {isCadmakers && (
          <Row>
            <Col md={6}>
              <FeedbackSizeDropdown
                value={this.state.selectedSize}
                onSelect={this.onSizeSelected}
                options={this.getSizeoptions()}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col lg={6}>
            <FeedbackFormTagsInput
              selectedTagIds={this.state.featureRequest.tagIds}
              onChangeTags={this.onChangeTags}
            />
          </Col>
          <Col lg={6}>
            <FeedbackFormIsPrivateInput
              isChecked={this.state.featureRequest.isPrivate}
              onChange={this.onChangeIsPrivate}
            />
          </Col>
        </Row>
        <FeedbackFormInput
          label="Describe the Feature"
          rows={3}
          type="textarea"
          name="description"
          value={this.state.featureRequest.description}
          onChange={e => this.onChange(e)}
          description="A description of the feature (e.g. purpose and functionality)"
        />
        <FeedbackFormInput
          label="Feature Requirements"
          rows={3}
          type="textarea"
          name="featureRequirements"
          value={this.state.featureRequest.featureRequirements}
          onChange={e => this.onChange(e)}
          description="A list of requirements for the requested feature."
        />

        <FormGroup>
          <Row>
            {isCadmakers && (
              <Col md={3}>
                <Label for="startDate">
                  <ElementSpacer spacing=".25rem">Start Date</ElementSpacer>
                </Label>
                <div style={{ width: 135 }}>
                  <CalendarPickerInput
                    value={
                      {
                        selectedDate: this.state.featureRequest.startDate,
                        actual: this.state.startDateDatePickerActual
                      } as any
                    }
                    onChange={this.onChangeStartDate}
                    onChangeMonth={this.onChangeStartDatepickerActual}
                    disabledDays={[
                      {
                        before: new Date()
                      }
                    ]}
                  />
                </div>
                <FormText>Start date for this task</FormText>
              </Col>
            )}
            <Col md={3}>
              <Label for="dueDate">
                <ElementSpacer spacing=".25rem">Due Date</ElementSpacer>
              </Label>
              <div style={{ width: 135 }}>
                <CalendarPickerInput
                  value={
                    {
                      selectedDate: this.state.featureRequest.dueDate,
                      actual: this.state.dueDateDatePickerActual
                    } as any
                  }
                  onChange={this.onChangeDueDate}
                  onChangeMonth={this.onChangeDueDatepickerActual}
                  disabledDays={[
                    {
                      before: new Date()
                    }
                  ]}
                />
              </div>
              <FormText>
                Due date for this feedback to be progressed to the next workflow status
              </FormText>
            </Col>
          </Row>
        </FormGroup>

        {isCadmakers && (
          <FormGroup>
            <Row>
              <Col md={2}>
                <FeedbackFormInput
                  label="Estimated Hours"
                  type="number"
                  name="estimatedHours"
                  autoComplete="off"
                  value={this.state.featureRequest.estimatedHours}
                  onChange={e => this.onChange(e)}
                  description="Estimated number of hours for this task."
                  min={0}
                />
              </Col>
            </Row>
          </FormGroup>
        )}
        <Row>
          <Col>
            <ImageDropzone
              images={this.state.images}
              onImagesAdded={this.onImagesAdded}
              onImageDelete={this.onImageDelete}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col>
            <FeedbackFileManager
              files={this.state.files.filter(f => !f.isCadmakersOnly)}
              onFilesAdded={this.onFilesAdded}
              onFileDelete={this.onFileDelete}
              isCadmakersOnly={false}
            />
          </Col>
        </Row>
        {isCadmakers && (
          <Row>
            <Col>
              <br />
              <FeedbackFileManager
                files={this.state.files.filter(f => f.isCadmakersOnly)}
                onFilesAdded={this.onFilesAdded}
                onFileDelete={this.onFileDelete}
                isCadmakersOnly
              />
            </Col>
          </Row>
        )}
        <hr />
        <Row>
          <Col xs="auto">
            <ElementSpacer>
              <Button disabled={!this.state.canSubmit}>Submit</Button>
              <Button onClick={this.clearForm}>Clear</Button>
              <FileUploadButton onClick={this.onImageUploadClick}>Upload Images</FileUploadButton>
              <FileUploadButton onClick={this.onFileUploadClick}>Upload Files</FileUploadButton>
              {isCadmakers && (
                <FileUploadButton onClick={this.onCadmakersFileUploadClick}>
                  Upload Cadmakers Internal Files
                </FileUploadButton>
              )}
            </ElementSpacer>
          </Col>
        </Row>
      </Form>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FeatureRequestForm));
